import { Component } from 'react';

/**
 * ErrorBoundary — last line of defence around a screen.
 *
 * Without one, a single bad render unmounts the entire React tree and the user
 * is left staring at an empty black rectangle with no way back — no tab bar, no
 * retry, nothing but a page reload. That is exactly what happened when the
 * sprint panel dereferenced a null race payload (Session 8).
 *
 * ScreenContainer mounts this keyed on the active tab, so switching tabs is
 * itself a recovery path; "Try again" resets in place for people who don't
 * realise that.
 *
 * Still a class component — React has no hook equivalent of
 * componentDidCatch/getDerivedStateFromError.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Kept on console rather than swallowed — this is the only trace a
    // developer gets once the boundary has hidden the failure from the UI.
    console.error('[ErrorBoundary]', this.props.label ?? 'screen', error, info?.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="flex min-h-full flex-col items-center justify-center px-8 py-16 text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-f1-red/10 ring-1 ring-f1-red/30">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-f1-red"
          >
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <circle cx="12" cy="17" r="0.5" fill="currentColor" />
          </svg>
        </div>
        <h3 className="text-lg font-bold text-ink-primary">This screen hit a snag</h3>
        <p className="mt-2 max-w-xs text-sm leading-relaxed text-ink-secondary">
          Something went wrong rendering this view. The rest of the app is still
          fine — switch tabs, or try again.
        </p>
        <p className="mt-3 max-w-xs break-words font-mono text-2xs text-ink-muted">
          {String(this.state.error?.message ?? this.state.error)}
        </p>
        <button
          onClick={() => this.setState({ error: null })}
          className="pressable mt-5 inline-flex items-center gap-2 rounded-full bg-f1-red px-5 py-2.5 text-sm font-semibold text-white shadow-glow-red-soft transition-colors hover:bg-f1-redBright"
        >
          Try again
        </button>
      </div>
    );
  }
}
